import { Inject, Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { EnterpriseApi } from 'api-typings/common/common';
import * as Rx from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  Feature,
  FeatureFlagConfig,
  featureFlagConfigurations,
  SiteTheme,
} from './feature-flags.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private flagConfig$: Rx.Observable<FeatureFlagConfig>;

  constructor(
    private datastore: Datastore,
    private auth: Auth,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {
    const { theme } = this.uiConfig;
    if (theme !== undefined && theme in featureFlagConfigurations) {
      this.flagConfig$ = Rx.of(featureFlagConfigurations[theme]);
      return;
    }

    const currentUserDoc = this.datastore.document(
      'users',
      this.auth.getUserId(),
    );
    // combine the status and value observables into an `Rx.Observable<User | undefined>`
    const loadedUser$ = currentUserDoc.status$.pipe(
      switchMap(status =>
        status.error ? Rx.of(undefined) : currentUserDoc.valueChanges(),
      ),
    );

    this.flagConfig$ = this.auth.isLoggedIn().pipe(
      switchMap(loggedIn =>
        loggedIn
          ? loadedUser$.pipe(
              map(user => {
                // Datastore errored, fallback to default theme
                if (!user) {
                  return featureFlagConfigurations[SiteTheme.DEFAULT];
                }

                if (
                  user.enterpriseIds !== undefined &&
                  !!user.enterpriseIds.find(
                    id => id === EnterpriseApi.DELOITTE_DC,
                  )
                ) {
                  return featureFlagConfigurations[SiteTheme.DELOITTE];
                }

                return featureFlagConfigurations[SiteTheme.DEFAULT];
              }),
            )
          : Rx.of(featureFlagConfigurations[SiteTheme.DEFAULT]),
      ),
    );
  }

  getFlag(feature: Feature): Rx.Observable<boolean> {
    return this.flagConfig$.pipe(map(config => config[feature]));
  }
}
